// frontend/src/services/permissionService.js
import axios from 'axios';
import { API_URL } from '../config/constants';
import { getTokenAuth } from '../utils/authUtils';
/**
 * Cache local des permissions et des rôles
 */
const permissionCache = {
  userPermissions: {},
  userRoles: [],
  roleDetails: {},
  lastUpdated: null,
  ttl: 5 * 60 * 1000 // 5 minutes
};

const pendingPermissionChecks = {};

/**
 * Récupérer le token d'authentification
 * @returns {string|null} - Token d'authentification ou null si non trouvé
 */
const getAuthToken = () => {
  // Essayer différentes clés de stockage possibles
  return getTokenAuth();
};

/**
 * Rafraîchir le cache des permissions
 * @returns {Promise<boolean>} - True si le rafraîchissement a réussi
 */
export const refreshPermissionCache = async () => {
  try {
    // Vérifier si l'utilisateur est connecté
    const token = getAuthToken();
    if (!token) {
      console.log('No authentication token found, skipping permission refresh');
      return false;
    }

    console.log('Refreshing permissions cache...');

    // Récupérer le profil complet de l'utilisateur avec rôles et permissions
    const response = await axios.get(
        `${API_URL}/auth/me/complete`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
    );

    const user = response.data.user || {};
    const permissions = user.permissions || [];
    const roles = user.roles || [];
    const roleDetails = user.roleDetails || [];

    console.log(`Permissions refreshed successfully: ${permissions.length} permissions loaded`);
    console.log(`Roles refreshed successfully: ${roles.length} roles loaded`);

    // Mettre à jour le cache des permissions
    permissionCache.userPermissions = {};
    permissions.forEach(permission => {
      permissionCache.userPermissions[permission] = true;
    });

    // Mettre à jour le cache des rôles
    permissionCache.userRoles = roles;

    // Mettre à jour le cache des détails des rôles
    permissionCache.roleDetails = {};
    roleDetails.forEach(role => {
      permissionCache.roleDetails[role.name] = role;
    });

    permissionCache.lastUpdated = Date.now();
    return true;
  } catch (error) {
    console.error('Error refreshing permission cache:', error);

    // Gestion spécifique des erreurs d'authentification
    if (error.response && error.response.status === 401) {
      console.warn('Authentication token expired or invalid');
      // Optionnel : vous pourriez déclencher une action pour déconnecter l'utilisateur
      // ou rediriger vers la page de connexion
    }

    return false;
  }
};

/**
 * Vérifier si l'utilisateur a une permission spécifique
 * @param {string} permissionName - Nom de la permission à vérifier
 * @returns {Promise<boolean>} - True si l'utilisateur a la permission
 */
export const checkPermission = async (permissionName) => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.log(`No authentication token found, denying permission: ${permissionName}`);
      return false;
    }

    // Vérifier si le cache est valide
    if (
        permissionCache.lastUpdated &&
        Date.now() - permissionCache.lastUpdated < permissionCache.ttl &&
        permissionCache.userPermissions[permissionName] !== undefined
    ) {
      console.log(`Using cached permission check for: ${permissionName} = ${permissionCache.userPermissions[permissionName]}`);
      return permissionCache.userPermissions[permissionName];
    }

    if (pendingPermissionChecks[permissionName]) {
      return await pendingPermissionChecks[permissionName];
    }

    console.log(`Checking permission with server: ${permissionName}`);
    pendingPermissionChecks[permissionName] = (async () => {
      const response = await axios.get(
          `${API_URL}/permissions/check/${permissionName}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      const hasPermission = response.data.hasPermission;
      console.log(`Server permission check result for ${permissionName}: ${hasPermission}`);

      permissionCache.userPermissions[permissionName] = hasPermission;
      permissionCache.lastUpdated = Date.now();

      return hasPermission;
    })();

    try {
      return await pendingPermissionChecks[permissionName];
    } finally {
      delete pendingPermissionChecks[permissionName];
    }
  } catch (error) {
    console.error(`Error checking permission ${permissionName}:`, error);

    if (
      error.response &&
      error.response.status === 429 &&
      permissionCache.userPermissions[permissionName] !== undefined
    ) {
      return permissionCache.userPermissions[permissionName];
    }

    // Gestion spécifique des erreurs d'authentification
    if (error.response && error.response.status === 401) {
      console.warn('Authentication token expired or invalid');
      // Optionnel : vous pourriez déclencher une action pour déconnecter l'utilisateur
    }

    return false;
  }
};

/**
 * Vérifier si l'utilisateur a un rôle spécifique
 * @param {string} roleName - Nom du rôle à vérifier
 * @returns {Promise<boolean>} - True si l'utilisateur a le rôle
 */
export const checkRole = async (roleName) => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.log(`No authentication token found, denying role: ${roleName}`);
      return false;
    }

    // Vérifier si le cache est valide
    if (
        permissionCache.lastUpdated &&
        Date.now() - permissionCache.lastUpdated < permissionCache.ttl
    ) {
      const hasRole = permissionCache.userRoles.includes(roleName);
      console.log(`Using cached role check for: ${roleName} = ${hasRole}`);
      return hasRole;
    }

    // Si le cache n'est pas valide, rafraîchir les permissions et rôles
    await refreshPermissionCache();

    // Vérifier si l'utilisateur a le rôle
    const hasRole = permissionCache.userRoles.includes(roleName);
    console.log(`Role check result for ${roleName}: ${hasRole}`);

    return hasRole;
  } catch (error) {
    console.error(`Error checking role ${roleName}:`, error);

    // Gestion spécifique des erreurs d'authentification
    if (error.response && error.response.status === 401) {
      console.warn('Authentication token expired or invalid');
    }

    return false;
  }
};

/**
 * Récupérer les détails d'un rôle
 * @param {string} roleName - Nom du rôle
 * @returns {Promise<Object|null>} - Détails du rôle ou null si non trouvé
 */
export const getRoleDetails = async (roleName) => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.log(`No authentication token found, cannot get role details: ${roleName}`);
      return null;
    }

    // Vérifier si le cache est valide
    if (
        permissionCache.lastUpdated &&
        Date.now() - permissionCache.lastUpdated < permissionCache.ttl &&
        permissionCache.roleDetails[roleName]
    ) {
      console.log(`Using cached role details for: ${roleName}`);
      return permissionCache.roleDetails[roleName];
    }

    // Si le cache n'est pas valide, rafraîchir les permissions et rôles
    await refreshPermissionCache();

    // Retourner les détails du rôle
    return permissionCache.roleDetails[roleName] || null;
  } catch (error) {
    console.error(`Error getting role details for ${roleName}:`, error);

    // Gestion spécifique des erreurs d'authentification
    if (error.response && error.response.status === 401) {
      console.warn('Authentication token expired or invalid');
    }

    return null;
  }
};

/**
 * Vérifier si un utilisateur a une permission spécifique
 * @param {string} userId - ID de l'utilisateur
 * @param {string} permissionName - Nom de la permission à vérifier
 * @returns {Promise<boolean>} - True si l'utilisateur a la permission
 */
export const userHasPermission = async (userId, permissionName) => {
  // Pour la compatibilité avec l'API existante, nous ignorons le userId
  // et utilisons simplement checkPermission qui vérifie l'utilisateur actuel
  return await checkPermission(permissionName);
};

/**
 * Vérifier si un utilisateur a un rôle spécifique
 * @param {string} userId - ID de l'utilisateur
 * @param {string} roleName - Nom du rôle à vérifier
 * @returns {Promise<boolean>} - True si l'utilisateur a le rôle
 */
export const userHasRole = async (userId, roleName) => {
  // Pour la compatibilité avec l'API existante, nous ignorons le userId
  // et utilisons simplement checkRole qui vérifie l'utilisateur actuel
  return await checkRole(roleName);
};

/**
 * Recherche de permissions
 * @param {Object} searchParams - Paramètres de recherche { query: string, category: string }
 * @returns {Promise<Array>} - Liste des permissions correspondantes
 */
const searchPermissions = async (searchParams) => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.log('No authentication token found, cannot search permissions');
      return [];
    }

    console.log('Searching permissions with params:', searchParams);

    const response = await axios.get(`${API_URL}/permissions/search`, {
      params: {
        query: searchParams.query,
        category: searchParams.category
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    console.log(`Found ${response.data.permissions.length} permissions`);
    return response.data.permissions;
  } catch (error) {
    console.error('Error searching permissions:', error);

    // Gestion spécifique des erreurs d'authentification
    if (error.response && error.response.status === 401) {
      console.warn('Authentication token expired or invalid');
    }

    return [];
  }
};


/**
 * Service pour gérer les permissions côté client
 */
const permissionService = {
  checkPermission,
  userHasPermission,
  checkRole,
  userHasRole,
  getRoleDetails,

  getAllPermissions: async () => {
    try {
      const token = getAuthToken();
      if (!token) return [];

      console.log('Fetching all permissions...');
      const response = await axios.get(
          `${API_URL}/permissions`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      console.log(`Fetched ${response.data.permissions.length} permissions`);
      return response.data.permissions;
    } catch (error) {
      console.error('Error getting permissions:', error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      return [];
    }
  },

  getMyPermissions: async () => {
    try {
      const token = getAuthToken();
      if (!token) return [];

      console.log('Fetching user permissions...');
      const response = await axios.get(
          `${API_URL}/permissions/my-permissions`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      console.log(`Fetched ${response.data.permissions.length} user permissions`);
      return response.data.permissions;
    } catch (error) {
      console.error('Error getting user permissions:', error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      return [];
    }
  },

  getMyRoles: async () => {
    try {
      const token = getAuthToken();
      if (!token) return [];

      // Vérifier si le cache est valide
      if (
          permissionCache.lastUpdated &&
          Date.now() - permissionCache.lastUpdated < permissionCache.ttl
      ) {
        console.log(`Using cached roles: ${permissionCache.userRoles.length} roles`);
        return permissionCache.userRoles;
      }

      // Si le cache n'est pas valide, rafraîchir les permissions et rôles
      await refreshPermissionCache();

      console.log(`Fetched ${permissionCache.userRoles.length} user roles`);
      return permissionCache.userRoles;
    } catch (error) {
      console.error('Error getting user roles:', error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      return [];
    }
  },

  getMyRoleDetails: async () => {
    try {
      const token = getAuthToken();
      if (!token) return [];

      // Vérifier si le cache est valide
      if (
          permissionCache.lastUpdated &&
          Date.now() - permissionCache.lastUpdated < permissionCache.ttl
      ) {
        const roleDetailsArray = Object.values(permissionCache.roleDetails);
        console.log(`Using cached role details: ${roleDetailsArray.length} roles`);
        return roleDetailsArray;
      }

      // Si le cache n'est pas valide, rafraîchir les permissions et rôles
      await refreshPermissionCache();

      const roleDetailsArray = Object.values(permissionCache.roleDetails);
      console.log(`Fetched ${roleDetailsArray.length} user role details`);
      return roleDetailsArray;
    } catch (error) {
      console.error('Error getting user role details:', error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      return [];
    }
  },

  getAllRoles: async () => {
    try {
      const token = getAuthToken();
      if (!token) return [];

      console.log('Fetching all roles...');
      const response = await axios.get(
          `${API_URL}/roles`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      console.log(`Fetched ${response.data.roles.length} roles`);
      return response.data.roles;
    } catch (error) {
      console.error('Error getting roles:', error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      return [];
    }
  },

  getRole: async (roleId) => {
    try {
      const token = getAuthToken();
      if (!token) return null;

      console.log(`Fetching role ${roleId}...`);
      const response = await axios.get(
          `${API_URL}/roles/${roleId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      console.log(`Fetched role: ${response.data.role.name}`);
      return response.data.role;
    } catch (error) {
      console.error(`Error getting role ${roleId}:`, error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      return null;
    }
  },

  createPermission: async (permissionData) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      console.log('Creating new permission:', permissionData);
      const response = await axios.post(
          `${API_URL}/permissions`,
          permissionData,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      // Rafraîchir le cache après la création d'une permission
      await refreshPermissionCache();

      console.log('Permission created successfully:', response.data.permission);
      return response.data.permission;
    } catch (error) {
      console.error('Error creating permission:', error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      throw error;
    }
  },

  updatePermission: async (id, permissionData) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      console.log(`Updating permission ${id}:`, permissionData);
      const response = await axios.patch(
          `${API_URL}/permissions/${id}`,
          permissionData,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      // Rafraîchir le cache après la mise à jour d'une permission
      await refreshPermissionCache();

      console.log('Permission updated successfully:', response.data.permission);
      return response.data.permission;
    } catch (error) {
      console.error(`Error updating permission ${id}:`, error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      throw error;
    }
  },

  deletePermission: async (id) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      console.log(`Deleting permission ${id}`);
      await axios.delete(
          `${API_URL}/permissions/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      // Rafraîchir le cache après la suppression d'une permission
      await refreshPermissionCache();

      console.log(`Permission ${id} deleted successfully`);
      return true;
    } catch (error) {
      console.error(`Error deleting permission ${id}:`, error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      throw error;
    }
  },

  createRole: async (roleData) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      console.log('Creating new role:', roleData);
      const response = await axios.post(
          `${API_URL}/roles`,
          roleData,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      // Rafraîchir le cache après la création d'un rôle
      await refreshPermissionCache();

      console.log('Role created successfully:', response.data.role);
      return response.data.role;
    } catch (error) {
      console.error('Error creating role:', error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      throw error;
    }
  },

  updateRole: async (id, roleData) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      console.log(`Updating role ${id}:`, roleData);
      const response = await axios.patch(
          `${API_URL}/roles/${id}`,
          roleData,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      // Rafraîchir le cache après la mise à jour d'un rôle
      await refreshPermissionCache();

      console.log('Role updated successfully:', response.data.role);
      return response.data.role;
    } catch (error) {
      console.error(`Error updating role ${id}:`, error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      throw error;
    }
  },

  deleteRole: async (id) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      console.log(`Deleting role ${id}`);
      await axios.delete(
          `${API_URL}/roles/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      // Rafraîchir le cache après la suppression d'un rôle
      await refreshPermissionCache();

      console.log(`Role ${id} deleted successfully`);
      return true;
    } catch (error) {
      console.error(`Error deleting role ${id}:`, error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      throw error;
    }
  },

  assignPermissionToRole: async (roleId, permissionId) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      console.log(`Assigning permission ${permissionId} to role ${roleId}`);
      const response = await axios.post(
          `${API_URL}/roles/${roleId}/permissions`,
          { permissionId },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      // Rafraîchir le cache après l'attribution d'une permission à un rôle
      await refreshPermissionCache();

      console.log(`Permission ${permissionId} assigned to role ${roleId} successfully`);
      return response.data;
    } catch (error) {
      console.error(`Error assigning permission ${permissionId} to role ${roleId}:`, error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      throw error;
    }
  },

  revokePermissionFromRole: async (roleId, permissionId) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      console.log(`Revoking permission ${permissionId} from role ${roleId}`);
      await axios.delete(
          `${API_URL}/roles/${roleId}/permissions/${permissionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      // Rafraîchir le cache après la révocation d'une permission d'un rôle
      await refreshPermissionCache();

      console.log(`Permission ${permissionId} revoked from role ${roleId} successfully`);
      return true;
    } catch (error) {
      console.error(`Error revoking permission ${permissionId} from role ${roleId}:`, error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      throw error;
    }
  },

  assignRoleToUser: async (userId, roleId) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      console.log(`Assigning role ${roleId} to user ${userId}`);
      const response = await axios.post(
          `${API_URL}/roles/assign`,
          { userId, roleId },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      // Rafraîchir le cache après l'attribution d'un rôle à un utilisateur
      await refreshPermissionCache();

      console.log(`Role ${roleId} assigned to user ${userId} successfully`);
      return response.data;
    } catch (error) {
      console.error(`Error assigning role ${roleId} to user ${userId}:`, error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      throw error;
    }
  },

  revokeRoleFromUser: async (userId, roleId) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      console.log(`Revoking role ${roleId} from user ${userId}`);
      await axios.delete(
          `${API_URL}/roles/revoke/${userId}/${roleId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      // Rafraîchir le cache après la révocation d'un rôle d'un utilisateur
      await refreshPermissionCache();

      console.log(`Role ${roleId} revoked from user ${userId} successfully`);
      return true;
    } catch (error) {
      console.error(`Error revoking role ${roleId} from user ${userId}:`, error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      throw error;
    }
  },

  getRoleUsers: async (roleId) => {
    try {
      const token = getAuthToken();
      if (!token) return [];

      console.log(`Fetching users for role ${roleId}...`);
      const response = await axios.get(
          `${API_URL}/roles/${roleId}/users`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
      );

      console.log(`Fetched ${response.data.users.length} users for role ${roleId}`);
      return response.data.users;
    } catch (error) {
      console.error(`Error getting users for role ${roleId}:`, error);

      // Gestion spécifique des erreurs d'authentification
      if (error.response && error.response.status === 401) {
        console.warn('Authentication token expired or invalid');
      }

      return [];
    }
  },
  searchPermissions, // Ajout de la nouvelle méthode
  refreshPermissionCache
};

export default permissionService;